import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { WidgetControlSetViewService } from "../api/widget-control-set-view.service";
import { VariableHubService } from "../../core/variables/variable-hub.service";
import { IVariableEmitter } from "../../core/variables/variable-emitter";
import { IVariableReceiver } from "../../core/variables/variable-receiver";
import { IEmittedVariable, IStaticVariable, IVariableRule } from "../../core/variables/variable-models";
import {
    InputType,
    IQueryDataRequest,
    PdfExportSettings,
    PdfExportSource,
    WidgetOutputMode
} from "../../core/reporting-models";
import { VariableRuleService } from "../../core/variables/variable-rule.service";
import {
    IControlSetDisplayView,
    IControlSetDisplayViewItem,
    IControlSetPdfTemplateSettings
} from "../widget-control-set.models";
import {
    CountableFilterMatchMode,
    FilterComponent,
    FilterInputType,
    FilterType,
    IFilterInfoBase,
    IFilterItem,
    IFilterSettings,
    QueryCommand,
    StringFilterMatchMode
} from "@features/filter";
import {
    WidgetControlSetPdfExportingService
} from "@main/reporting.v3/widget.control-set/api/widget-control-set-pdf-exporting.service";
import {
    WidgetControlSetAuxiliaryService
} from "@main/reporting.v3/widget.control-set/api/widget-control-set-auxiliary.service";
import { WidgetControlSetDataService } from "@main/reporting.v3/widget.control-set/api/widget-control-set-data.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { BehaviorSubject, firstValueFrom, takeUntil, Subject } from "rxjs";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { IFilter } from "@features/filter/interfaces/filter";
import { SelectItem } from "primeng/api";
import { IHash } from "@bbwt/interfaces";
import { v4 as uuidv4 } from "uuid";


@Component({
    selector: "widget-control-set",
    styleUrls: ["widget-control-set.component.scss"],
    templateUrl: "./widget-control-set.component.html"
})
export class WidgetControlSetComponent implements IWidgetComponent, IVariableEmitter, IVariableReceiver, IPdfExportable, OnDestroy {
    // Widget settings.
    public readonly widgetType: string = "control-set";
    public readonly widgetSelector: string = "widget-control-set";

    // Control set settings.
    protected filtersBackup: IFilter = null;
    protected filterSettings: IFilterSettings[] = [];

    // Variable handling settings.
    public readonly variableEmitterId: string = uuidv4();
    public readonly variableReceiverId: string = uuidv4();

    // Pdf view settings.
    protected renderPdfViewHidden: boolean;

    // Loading settings.
    public pdfExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Subject to notify the destruction of this component.
    private destroyed$: Subject<void> = new Subject<void>();

    // Component fields.
    public widgetTitle: string;

    private variables: IEmittedVariable[];
    private filterVariables: IEmittedVariable[];
    private dropdownVariableNullValueToken: string = uuidv4();
    private controlSetItemByVariableMap: IHash<IControlSetDisplayViewItem>;

    private _widgetSourceId: string;
    private _widgetVisible: boolean;
    private _output: WidgetOutputMode = "web";
    private _staticVariables: IStaticVariable[];
    private _pdfViewFilters: ElementRef<HTMLElement>;
    private _controlSetDisplayView: IControlSetDisplayView;

    protected readonly FilterInputType: typeof FilterInputType = FilterInputType;
    protected readonly CountableFilterMatchMode: typeof CountableFilterMatchMode = CountableFilterMatchMode;

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Determines if grid should ignore its own displaying rules.
    @Input() ignoreDisplayRule: boolean;

    // Emitter to notify that pdf view is ready for exporting.
    @Output() readonly pdfExportingReady: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(FilterComponent, { static: false }) private filterComponent: FilterComponent;

    constructor(private widgetControlSetPdfExportingService: WidgetControlSetPdfExportingService,
                private widgetControlSetAuxiliaryService: WidgetControlSetAuxiliaryService,
                private widgetControlSetViewService: WidgetControlSetViewService,
                private widgetControlSetDataService: WidgetControlSetDataService,
                private variableRuleService: VariableRuleService,
                private variableHubService: VariableHubService,
                private broadcastService: BroadcastService,
                private elementRef: ElementRef) {
        // TODO: This logic may no longer be necessary, as patient switch now emits
        //  patient_id as variable when selected patient changes. This widget will
        //  capture this variable in receiveEmittedVariables and refresh corresponding
        //  dropdowns accordingly. Retained pending verification.
        this.broadcastService
            .on<string>(PatientService.CurrentPatientChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(_ => this.reloadControlSet());
    }

    // Captures pdf view container and notify when it is ready.
    @ViewChild("pdfViewFilters") protected set pdfViewFilters(value: ElementRef<HTMLElement>) {
        if (!!value) {
            // Wait a little time for the div to render.
            setTimeout((): void => {
                this._pdfViewFilters = value;
                this.pdfExportingReady.emit();
            }, 100);
        } else this._pdfViewFilters = null;
    }

    protected get pdfViewFilters(): ElementRef<HTMLElement> {
        return this._pdfViewFilters;
    }

    // Determines which html code should be used for rendering: web view of pdf exporting view.
    @Input() set output(value: WidgetOutputMode) {
        // If the view is changed to PDF view, refresh filters backup with current filters.
        if (value === "PDF") this.filtersBackup = this.filterComponent?.filters;

        this._output = value ?? "web";
    }

    get output(): WidgetOutputMode {
        return this._output;
    }

    // Determines if grid is able to emit variables.
    @Input() set variableEmitter(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableEmitter(this);
        else this.variableHubService.unregisterVariableEmitter(this);
    }

    // Determines if grid is able to receive variables.
    @Input() set variableReceiver(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableReceiver(this);
        else this.variableHubService.unregisterVariableReceiver(this);
    }

    // Set of fixed-value variables to consider during dropdown data fetching.
    @Input() set staticVariables(value: IStaticVariable[]) {
        if (!value) return;
        this._staticVariables = value;
        this.refreshWidgetView();
    }

    get staticVariables(): IStaticVariable[] {
        return this._staticVariables;
    }

    // View of the control set. Refresh control set settings every time this variable changes.
    @Input() set controlSetDisplayView(value: IControlSetDisplayView) {
        if (!value) return;
        this._controlSetDisplayView = value;
        this._widgetSourceId = value.widgetSourceId;
        this.refreshWidgetView(true);
    };

    get controlSetDisplayView(): IControlSetDisplayView {
        return this._controlSetDisplayView;
    }

    // Widget source of the grid. Refresh all widget every time this variable changes.
    @Input() set widgetSourceId(value: string) {
        if (this.widgetSourceId === value) return;
        this._widgetSourceId = value;
        this.refreshWidget().then();
    }

    get widgetSourceId(): string {
        return this._widgetSourceId;
    }

    @Input() set widgetVisible(value: boolean) {
        this._widgetVisible = value;
    }

    get widgetVisible(): boolean {
        return this.ignoreDisplayRule || this._widgetVisible;
    }

    get widgetName(): string {
        return this.controlSetDisplayView?.widgetSource?.name;
    }

    get widgetCode(): string {
        return this.controlSetDisplayView?.widgetSource?.code;
    }

    get pdfViewVisible(): boolean {
        const empty: boolean = !this.filters || Object.keys(this.filters).every(key => !this.filters[key].value);
        return this.widgetVisible && !empty;
    }

    get filters(): IFilter {
        return this.filterComponent?.filters ?? this.filtersBackup ?? {};
    }

    get controlSetFiltersClass(): string {
        return this.widgetControlSetAuxiliaryService.controlSetFiltersClass;
    }

    get widgetContainerClass(): string {
        return this.widgetControlSetAuxiliaryService.widgetContainerClass;
    }


    @HostListener("window:scroll", []) protected onWindowScroll(): void {
        // Perform a click in the widget to trigger dropdown hiding.
        this.elementRef.nativeElement.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }


    public ngOnDestroy(): void {
        this.variableHubService.unregisterVariableReceiver(this);
        this.variableHubService.unregisterVariableEmitter(this);
        this.pdfExportingLoading.complete();

        this.destroyed$.next();
        this.destroyed$.complete();
    }

    // Variable management methods.
    public receiveEmittedVariables(variables: IEmittedVariable[]): void {
        // Save current state of variables.
        const oldVariables: IEmittedVariable[] = this.variables ?? [];
        this.variables = variables;

        // If there is no widget yet, take all variables as last emitted variables.
        // Otherwise, take as last emitted variables only the variables related to this widget.
        const oldFilterVariables: IEmittedVariable[] = this.filterVariables ?? [];
        const filterVariableNames: string[] = this.controlSetDisplayView?.items
            ?.map(item => item.variableName);

        this.filterVariables = this.variableRuleService.filterVariableSet(filterVariableNames, variables);

        if (!this.controlSetDisplayView) return;

        // Show/hide widget if required.
        const displayRule: IVariableRule = this.controlSetDisplayView.widgetSource?.displayRule;

        this.widgetVisible = !displayRule || this.variableRuleService.containsMatch(displayRule, variables);

        if (!this.variableRuleService.equalVariableSets(this.variables, oldVariables)) this.refreshTitle();

        // Refresh the widget if some related variable changed its value.
        if (this.variableRuleService.equalVariableSets(this.filterVariables, oldFilterVariables)) this.reloadControlSet().then();
    }

    // Refreshing methods.
    private async refreshWidget(): Promise<void> {
        if (!this.widgetSourceId) return;
        this.controlSetDisplayView = await this.widgetControlSetViewService.getDisplayView(this.widgetSourceId);
    }

    private refreshControlSetSettings(): void {
        if (!this.controlSetDisplayView) return;

        this.widgetVisible = this.ignoreDisplayRule || !this.controlSetDisplayView.widgetSource?.displayRule;

        this.controlSetItemByVariableMap = {};
        const items: IControlSetDisplayViewItem[] = this.controlSetDisplayView.items ?? [];
        items.forEach(item => this.controlSetItemByVariableMap[item.variableName] = item);

        this.filterSettings = items.map(item => {
            const filterType: FilterType = this.widgetControlSetAuxiliaryService.getFilterTypeByDataType(item.dataType);

            return <IFilterSettings>{
                id: item.name,
                order: item.sortOrder,
                header: item.hintText,
                inputType: item.inputType,
                valueFieldName: item.variableName,
                ignoreIfConvertibleToFalse: item.emptyFilterIfFalse,

                filterType: filterType,
                applyFilterIfNullValue: true,
                applyFilterIfEmptyValue: true,
                matchModeSelectorVisible: false
            }
        });

        this.refreshDropdownData(items, this.filterVariables).then();
    }

    private refreshTitle(): void {
        if (!this.controlSetDisplayView?.widgetSource?.title) return;
        let processedTitle = this.variableRuleService.embedVariableValues(this.controlSetDisplayView.widgetSource?.title, this.variables);
        this.widgetTitle = this.variableRuleService.cleanupUnmatchedVariables(processedTitle);
    }

    private refreshWidgetView(complete: boolean = false): void {
        if (complete) {
            this.refreshControlSetSettings();
        } else {
            void this.reloadControlSet();
        }
        this.refreshTitle();
    }

    private async refreshDropdownData(items: IControlSetDisplayViewItem[], variables: IEmittedVariable[]): Promise<void> {
        for (const item of items) {
            if (!this.widgetControlSetAuxiliaryService.isDropdownType(item.inputType)) continue;

            const options: SelectItem[] = await this.getDropdownData(item, variables);
            const settings: IFilterSettings = this.filterSettings.find(settings => settings.id === item.name);
            const filter: IFilterItem = this.filterComponent?.filters[settings.valueFieldName];

            if (!!settings) settings.dropdownOptions = options;
            if (!filter) continue;

            const validValue = (value: any) => options.find(option => option.value === value);

            // If input is dropdown but the current value is not between the options, clear the input.
            if (item.inputType === "dropdown" && !!filter.value && !validValue(filter.value)) {
                filter.value = undefined;
                this.onFilterChange(filter);
            }

            // If input is multiselect, clear the input from values that are not between the options.
            if (item.inputType === "multiselect" && !!filter.value?.length && !filter.value?.every(validValue)) {
                filter.value = filter.value?.filter(validValue);
                this.onFilterChange(filter);
            }
        }
    }

    private async reloadControlSet(): Promise<void> {
        if (!this.controlSetDisplayView) return;

        const variables: IEmittedVariable[] = this.getWidgetVariables();

        await this.refreshDropdownData(this.controlSetDisplayView.items, variables);
    }

    protected async restoreFiltersFromBackup(filters: IFilter): Promise<void> {
        if (!this.filtersBackup) return;

        Object.keys(this.filtersBackup).forEach(key => filters[key] = this.filtersBackup[key]);

        this.filtersBackup = null;
    }

    // Auxiliary methods.
    private async getDropdownData(item: IControlSetDisplayViewItem, variables: IEmittedVariable[]): Promise<SelectItem[]> {
        if (!this.widgetControlSetAuxiliaryService.isDropdownType(item.inputType)) return null;

        const queryDataRequest: IQueryDataRequest = {
            tableId: item.tableId,
            folderId: item.folderId,
            sourceCode: item.sourceCode,
            parentTableId: item.parentTableId,
            labelColumnId: item.labelColumnId,
            valueColumnId: item.valueColumnId,
            filterOperand: item.filterRule?.operand,
            filterOperator: item.filterRule?.operator,
            filterColumnId: item.filterRule?.tableColumnId,
            queryVariables: { variables: variables ?? [] },
            widgetSourceId: this.widgetSourceId
        }

        const error: string = "An unexpected error occurred fetching the dropdown data. Please review the filter value and the filtering operation.";
        const items: SelectItem[] = await this.widgetControlSetDataService.getDropdownData(queryDataRequest, { errorStatusesMessages: { 500: error } });

        return items.map(item => {
            const label: string = this.widgetControlSetAuxiliaryService.getDisplayableLabel(item.label);
            const value: any = item.value ?? this.dropdownVariableNullValueToken
            return <SelectItem>{ label: label, value: value }
        });
    }

    private getVariableValueFromFilterInfo(filterItem: IFilterItem, filterInfo?: IFilterInfoBase): IEmittedVariable {
        const field: string = filterItem.field;
        const settings: IFilterSettings = filterItem.settings;
        const inputType: InputType = settings?.inputType ?? "text";
        const filterType: FilterType = settings?.filterType ?? FilterType.Text;

        let value: any = filterItem.value;
        let empty: boolean = value == null;
        let matchMode: CountableFilterMatchMode | StringFilterMatchMode = filterItem.matchMode;

        // If input mode is multiselect, change match mode to "In".
        // Substitute null-tokens in the selection by null values.
        if (inputType == FilterInputType.Multiselect) {
            matchMode = filterType == FilterType.Numeric ? CountableFilterMatchMode.In : StringFilterMatchMode.In;
            value = value?.map((item: any) => item === this.dropdownVariableNullValueToken ? null : item);
            empty = !value?.length;
        }

        // If input mode is dropdown, and the selected value is the null-token, substitute it by null value.
        if (inputType == FilterInputType.Dropdown && value === this.dropdownVariableNullValueToken) {
            value = null;
        }

        // If input mode is checkbox, value is false, and false values are treated as empty, clear the value.
        if (inputType == FilterInputType.Checkbox && !value && !!settings?.ignoreIfConvertibleToFalse) {
            value = null;
            empty = true;
        }

        // Get filter info corresponding to this filter if is not passed as parameter.
        filterInfo ??= QueryCommand.createFilter(field, value, filterType, matchMode);

        // Convert "between" filter types to array variable types, as variables
        // doesn't support match mode (it makes sense in filters but not in variables).
        const type: string = filterInfo.$type.replace(/Between$/, "Array");

        return {
            name: field,
            value: value,
            empty: empty,
            $type: type,
            data: filterInfo,
            behaviorOnEmpty: "populate"
        };
    }

    public getFilterValue(settings: IFilterSettings): any {
        return this.widgetControlSetAuxiliaryService.getFilterValue(this.filters, settings);
    }

    private getWidgetVariables(): IEmittedVariable[] {
        const staticVariables: IEmittedVariable[] = this.staticVariables?.map(variable =>
            <IEmittedVariable>{ ...variable, empty: false, behaviorOnEmpty: "populate" }) ?? [];

        return staticVariables.concat(this.filterVariables ?? []);
    }

    protected onFilter(filters: IFilterInfoBase[]): void {
        const variables: IEmittedVariable[] = filters
            .filter(filterInfo => this.controlSetItemByVariableMap[filterInfo.propertyName]?.valueEmitType)
            .map(filterInfo => this.getVariableValueFromFilterInfo(this.filters[filterInfo.propertyName], filterInfo));
        this.variableHubService.emitVariables(this.variableEmitterId, variables);
    }

    protected onFilterChange(filterItem: IFilterItem): void {
        // Search the control-set-item corresponding to this filter.
        const item: IControlSetDisplayViewItem = this.controlSetItemByVariableMap[filterItem.field];
        if (!item) return;

        if (item.valueEmitType === "singleAndGrouped") {
            // If this control-set-item is single-and-grouped, just trigger filtering.
            const filters: IFilterInfoBase[] = this.filterComponent.getFilters() ?? [];
            this.onFilter(filters);

        } else if (item.valueEmitType === "standalone") {
            // If this control-set-item is standalone, emit this variable.
            const variable: IEmittedVariable = this.getVariableValueFromFilterInfo(filterItem);
            this.variableHubService.emitVariables(this.variableEmitterId, [variable]);

        } else if (item.valueEmitType === "grouped") {
            // Otherwise, if it is grouped, refresh control-set-items related to this variable.
            const variable: IEmittedVariable = this.getVariableValueFromFilterInfo(filterItem);

            // Filter items to get items related to this variable.
            const items: IControlSetDisplayViewItem[] = this.controlSetDisplayView.items
                .filter(item => this.widgetControlSetAuxiliaryService.isMatch(item.filterRule, variable));

            this.refreshDropdownData(items, [variable]).then();
        }
    }

    // Pdf exporting methods.
    private async renderPdfViewForFunction<T>(func: () => Promise<T>): Promise<T> {
        if (!!this.pdfViewFilters) return func();

        // Render pdf view hidden, and then execute parameter function.
        this.renderPdfViewHidden = true;
        await firstValueFrom(this.pdfExportingReady);

        return func().finally(() => this.renderPdfViewHidden = false);
    }

    private getControlSetPdfTemplateSettings(): IControlSetPdfTemplateSettings {
        return {
            template: this.elementRef?.nativeElement?.innerHTML,
            controlSetFiltersTemplate: this.pdfViewFilters?.nativeElement?.outerHTML,
        };
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        if (!this.pdfViewFilters) return this.renderPdfViewForFunction(this.getPdfConfiguration.bind(this));

        const settings: IControlSetPdfTemplateSettings = this.getControlSetPdfTemplateSettings();
        return await this.widgetControlSetPdfExportingService.getPdfConfiguration(settings);
    }

    public async generatePdf(): Promise<void> {
        if (this.pdfExportingLoading.value) return;

        try {
            this.pdfExportingLoading.next(true);

            const configuration: JsReportConfiguration = await this.getPdfConfiguration();

            const settings: PdfExportSettings = {
                source: PdfExportSource.WidgetControlSet,
                configuration: configuration,
                fileName: this.widgetName
            }

            await this.widgetControlSetPdfExportingService.requestPdfExport(this.widgetSourceId, settings);

        } finally {
            this.pdfExportingLoading.next(false);
        }
    }
}